import Tagify from '@yaireo/tagify';
import {
  mergedTagifyMixValueFromPaste,
  pasteTargetIsTagifyInnerField
} from './clipboard.js';
import {
  prepareUnclosedMixTagOpeners,
  restoreUnclosedMixTagOpeners
} from './tagify_unclosed_mix_openers.js';

window.Tagify = Tagify

// Track which tagify input should prevent blur submit (set on mousedown, cleared on mouseup)
let preventBlurSubmitForInput = null;

// Global mousedown handler - fires BEFORE blur, so we can set the flag in time
document.addEventListener('mousedown', (event) => {
  // Check if mousedown is on elements that should prevent blur submit
  const isInTagifyDropdown = event.target.closest('.tagify__dropdown__item') !== null;
  const isInSchemaDropdown = event.target.closest('.schema-field-dropdown') !== null;
  const isAddTagButton = event.target.closest('.add-tag-button') !== null;
  const tagifyWrapper = event.target.closest('.tagify-parent-wrapper');

  // If clicking the add-tag button, prevent blur for that wrapper's tagify
  if (isAddTagButton && tagifyWrapper) {
    const tagifyContainer = tagifyWrapper.querySelector('.tagify');
    if (tagifyContainer) {
      preventBlurSubmitForInput = tagifyContainer;
    }
    return;
  }

  if (isInTagifyDropdown || isInSchemaDropdown) {
    // Find the tagify input this dropdown belongs to
    const schemaDropdown = event.target.closest('.schema-field-dropdown');
    if (schemaDropdown) {
      const targetName = schemaDropdown.getAttribute('data-tagify-input');
      if (targetName) {
        const tagifyInputs = document.querySelectorAll('.tagify');
        for (const input of tagifyInputs) {
          if (input.dataset.targetName === targetName) {
            preventBlurSubmitForInput = input;
            break;
          }
        }
      } else {
        // No specific target, prevent for all tagify inputs (use a marker)
        preventBlurSubmitForInput = 'all';
      }
    } else {
      // Tagify dropdown item - find which tagify it belongs to
      const tagifyInputs = document.querySelectorAll('.tagify');
      for (const input of tagifyInputs) {
        if (input.__tagify) {
          let dropdown = input.__tagify.dropdown?.DOM?.dropdown || input.__tagify.dropdown?.dropdown;
          if (dropdown && dropdown.contains(event.target)) {
            preventBlurSubmitForInput = input;
            break;
          }
        }
      }
    }
  }
}, true);

// Global mouseup handler - reset the flag after the click completes
document.addEventListener('mouseup', () => {
  // Small delay to ensure blur handler has time to check the flag
  setTimeout(() => {
    preventBlurSubmitForInput = null;
  }, 100);
}, true);

export function canMakeTagifyForStepInput() {
  return !!getPreviousStepsEl()
}

function getPreviousStepsEl() {
  return document.getElementById('previous_steps')
}

// Helper function to find the most recently added tag and click its inner input
function clickMostRecentTagInnerInput(tagify) {
  setTimeout(() => {
    const allTags = tagify.DOM.input.querySelectorAll('.tagify__tag');

    // Find the most recently added tag by finding the one with the highest timestamp in its ID
    const mostRecentTag = Array.from(allTags).reduce((newest, current) => {
      const currentTimestamp = parseInt(current.id.split('-').pop());
      const newestTimestamp = parseInt(newest.id.split('-').pop());
      return currentTimestamp > newestTimestamp ? current : newest;
    });

    if (mostRecentTag) {
      const innerInput = mostRecentTag.querySelector('.tagify-inner-input');
      if (innerInput) {
        innerInput.click();
      }
    }
  }, 10);
}

// Centralized dropdown management
class DropdownManager {
  static closeAll() {
    const existingDropdowns = document.querySelectorAll('.schema-field-dropdown');
    existingDropdowns.forEach(dropdown => {
      if (dropdown.parentElement) {
        dropdown.remove();
      }
    });

    // Remove highlighting from all tags
    document.querySelectorAll('.tagify__tag').forEach(tag => {
      tag.classList.remove('schema-dropdown-active');
    });
  }

  static highlightTag(tagElement) {
    // Remove highlighting from all tags first
    document.querySelectorAll('.tagify__tag').forEach(tag => {
      tag.classList.remove('schema-dropdown-active');
    });

    // Highlight the active tag
    if (tagElement) {
      tagElement.classList.add('schema-dropdown-active');
    }
  }
}

class TagDataExtractor {
  static extract(tagElement) {
    // Try to get data from Tagify's internal data first
    let tagData = tagElement.__tagifyTagData;

    if (!tagData) {
      // For existing tags, get data from attributes
      const stepId = tagElement.getAttribute('step_id');
      const value = tagElement.getAttribute('value');
      const name = tagElement.getAttribute('name');
      const schemaDefined = tagElement.getAttribute('schema_defined');
      const position = tagElement.getAttribute('position');

      if (stepId && value && schemaDefined === 'true') {
        tagData = {
          step_id: stepId,
          value: value,
          name: name,
          position: position,
          type: 'step',
          schema_defined: true
        };
      }
    }

    return tagData;
  }

  static isValidForSchemaDropdown(tagData) {
    if (!tagData || !tagData.schema_defined) return false;

    return (tagData.type === 'step' || tagData.value === 'variables');
  }
}

class TagifyInstanceFinder {
  static findForTag(tagElement) {
    const allTagifyInputs = document.querySelectorAll('.tagify');

    for (let input of allTagifyInputs) {
      if (input.contains(tagElement)) {
        const tagifyParent = input.parentElement;
        const regularInput = tagifyParent ? tagifyParent.querySelector('input[type="text"].tagify') : null;
        return regularInput ? regularInput.__tagify : null;
      }
    }

    return null;
  }
}

// Handle schema dropdown for a tag element
function handleTagSchemaDropdown(tagElement, tagifyInstance) {
  if (!tagElement) return;

  const tagData = TagDataExtractor.extract(tagElement);

  if (TagDataExtractor.isValidForSchemaDropdown(tagData)) {
    DropdownManager.closeAll();

    if (tagifyInstance) {
      showSchemaFields(tagData, tagifyInstance, tagElement);
    }
  }
}

// Handle blur event for tagify input - submit form unless clicking on dropdowns
function handleTagifyBlur(inputElement, tagifyInstance) {
  // Don't submit if we're preventing blur submit (e.g., when clicking dropdown button)
  if (inputElement.dataset.preventBlurSubmit === 'true') {
    return;
  }

  // Store timeout ID so we can cancel it if focus returns
  const timeoutId = setTimeout(() => {
    const activeElement = document.activeElement;

    // Check if the active element IS the input element itself (user clicked back into it)
    if (activeElement === inputElement) {
      return;
    }

    // Check if active element is within the tagify container
    const tagifyContainer = inputElement.closest('.tagify');
    if (tagifyContainer && tagifyContainer.contains(activeElement)) {
      return;
    }

    // Check if active element is within the same tagify wrapper (e.g., add-tag button)
    const tagifyWrapper = inputElement.closest('.tagify-parent-wrapper');
    if (tagifyWrapper && tagifyWrapper.contains(activeElement)) {
      return;
    }

    // Check if mousedown was on a dropdown for this input (set by global mousedown handler)
    const shouldPreventForThisInput = preventBlurSubmitForInput === 'all' ||
      preventBlurSubmitForInput === tagifyContainer;

    // Also check if active element is currently in a dropdown (fallback)
    const isInDropdown = activeElement?.closest('.schema-field-dropdown') !== null ||
      activeElement?.closest('.tagify__dropdown') !== null;

    if (shouldPreventForThisInput || isInDropdown) {
      return;
    }

    // Submit the form
    const wrapper = inputElement.closest('.tagify-parent-wrapper');
    if (!wrapper) return;

    let targetElement = null;

    if (inputElement.dataset.targetName) {
      const targetName = inputElement.dataset.targetName;
      const allHiddenInputs = wrapper.querySelectorAll('input[type="hidden"]');

      for (const input of allHiddenInputs) {
        if (input.name === targetName && input.closest('.tagify-parent-wrapper') === wrapper) {
          targetElement = input;
          break;
        }
      }
    }

    if (targetElement) {
      const form = targetElement.closest('form');
      if (form) {
        // Check if form is already submitting to prevent double submission
        if (form.dataset.tagifySubmitting === 'true') {
          return;
        }

        // Mark form as submitting
        form.dataset.tagifySubmitting = 'true';

        // Save scroll position before submitting to prevent page from scrolling up
        const scrollPosition = window.scrollY || window.pageYOffset;
        sessionStorage.setItem('tagifyScrollPosition', scrollPosition.toString());

        form.requestSubmit();

        // Clear the submitting flag after a delay
        setTimeout(() => {
          delete form.dataset.tagifySubmitting;
        }, 1000);
      }
    }
  }, 50); // Short delay to let mousedown handler set the flag

  // Store timeout ID on input element so we can cancel it
  inputElement._tagifyBlurTimeout = timeoutId;
}

export function makeTagifyForStepInput(inputElement) {
  if (!canMakeTagifyForStepInput()) {
    return
  }

  // Check if element already has a tagify instance and destroy it first
  // This prevents double-initialization when Turbo updates the DOM
  if (inputElement.__tagify) {
    try {
      inputElement.__tagify.destroy();
    } catch (e) {
      // Silently ignore errors
    }
    delete inputElement.__tagify;
  }

  // Verify that we're initializing on an input element, not a tags element
  if (inputElement.tagName === 'TAGS') {
    return;
  }

  const previousStepsEl = getPreviousStepsEl();
  const settings = buildSettings(previousStepsEl.dataset.previous_steps,
                                 previousStepsEl.dataset.variable,
                                 previousStepsEl.dataset.update_reference_fields
                                )

  let tagify = new Tagify(inputElement, settings);

  // Set turbo_permanent on the main input element that Tagify creates
  // This ensures cursor position is preserved during Turbo updates
  if (tagify.DOM && tagify.DOM.input) {
    tagify.DOM.input.setAttribute('data-turbo-permanent', 'true');
    tagify.DOM.input.addEventListener(
      'paste',
      (domEvent) => {
        if (pasteTargetIsTagifyInnerField(domEvent)) return;

        const merged = mergedTagifyMixValueFromPaste(
          domEvent,
          buildInputValue(tagify.DOM.input)
        );
        if (merged == null) return;

        domEvent.preventDefault();
        domEvent.stopImmediatePropagation();
        domEvent.stopPropagation();

        reloadTagifyMixFromStoredString(tagify, merged, { syncHidden: true });
      },
      true
    );

    // Mix-mode: Tagify may sync this.value + update() on backspace/delete without firing its
    // custom "input" event (onMixTagsInput early return). Native input still runs — keep the
    // hidden field in sync so saves match what the user sees (X uses "remove", which we handle).
    tagify.DOM.input.addEventListener('input', () => {
      tagifyChange({ detail: { tagify } });
    });
  }

  // Also set turbo_permanent on the tagify container itself
  if (inputElement.parentElement) {
    inputElement.parentElement.setAttribute('data-turbo-permanent', 'true');
  }

  tagify.on('input', tagifyChange)
    .on('add', (event) => {
      tagifyChange(event);
      const tag = event.detail.data;

      if (tag.schema_defined) {
        clickMostRecentTagInnerInput(tagify);
      }

      // Resize input fields after adding a tag
      setTimeout(() => {
        const innerInputs = inputElement.parentElement.querySelectorAll('.tagify-inner-input');
        innerInputs.forEach(input => {
          refreshSize(input);
        });
      }, 10);
    })
    .on('remove', tagifyChange)
    .on('dropdown:select', (event) => {
      const tag = event.detail.data;

      if (tag.schema_defined) {
        clickMostRecentTagInnerInput(tagify);
      }
    })
    .on('paste', (event) => {
      // Prevent Tagify's default paste behavior when we're in an inner input
      if (event.detail && event.detail.target && event.detail.target.classList.contains('tagify-inner-input')) {
        event.preventDefault();
        event.stopPropagation();
      }
    })
    .on('blur', () => {
      // Submit form on blur, but not if clicking on dropdowns
      handleTagifyBlur(inputElement, tagify);
    })
    .on('focus', () => {
      // Cancel any pending blur submit if user clicks back into the input
      if (inputElement._tagifyBlurTimeout) {
        clearTimeout(inputElement._tagifyBlurTimeout);
        inputElement._tagifyBlurTimeout = null;
      }
    })

  // Add click handler for the add tag button
  setTimeout(() => {
    const addTagButton = inputElement.parentElement.querySelector('.add-tag-button');

    if (addTagButton) {
      addTagButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        // Close any open schema dropdowns before showing step dropdown
        DropdownManager.closeAll();

        tagify.DOM.input.focus();
        tagify.dropdown.show.call(tagify, '');
      });
    }
  }, 10);

  // Ensure proper sizing after Tagify is initialized
  setTimeout(() => {
    const innerInputs = inputElement.parentElement.querySelectorAll('.tagify-inner-input');
    innerInputs.forEach(input => {
      refreshSize(input);
    });
  }, 50);

  // Add MutationObserver to handle dynamically created input fields
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      // Handle added nodes
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          // Check if a new tag was added (for fields without inner input field or value)
          if (node.classList && node.classList.contains('tagify__tag')) {
            setTimeout(() => {
              // Find the hidden input using ID-based lookup
              const originalInput = tagify.DOM.originalInput;
              const wrapper = originalInput.closest('.tagify-parent-wrapper');
              if (!wrapper) return;

              let targetElement = null;

              // Use target_id if available otherwise fall back to target_name
              if (originalInput.dataset.targetId) {
                targetElement = document.getElementById(originalInput.dataset.targetId);
                // Verify it's in the same wrapper
                if (targetElement && targetElement.closest('.tagify-parent-wrapper') !== wrapper) {
                  targetElement = null;
                }
              }

              // Fallback to name-based lookup if ID not available
              if (!targetElement && originalInput.dataset.targetName) {
                const targetName = originalInput.dataset.targetName;
                const allHiddenInputs = wrapper.querySelectorAll('input[type="hidden"]');

                for (const input of allHiddenInputs) {
                  if (input.name === targetName && input.closest('.tagify-parent-wrapper') === wrapper) {
                    targetElement = input;
                    break;
                  }
                }
              }

              if (targetElement) {
                targetElement.value = buildInputValue(tagify.DOM.input);
              }
            }, 0);
          }

          const newInputs = node.querySelectorAll ? node.querySelectorAll('.tagify-inner-input') : [];
          newInputs.forEach(input => {
            // Set turbo_permanent on inner inputs
            input.setAttribute('data-turbo-permanent', 'true');
            setTimeout(() => refreshSize(input), 0);
          });

          // If the node itself is an input
          if (node.classList && node.classList.contains('tagify-inner-input')) {
            node.setAttribute('data-turbo-permanent', 'true');
            setTimeout(() => refreshSize(node), 0);
          }
        }
      });

      // Handle attribute changes (like value changes)
      if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
        const target = mutation.target;
        if (target.classList && target.classList.contains('tagify-inner-input')) {
          setTimeout(() => refreshSize(target), 0);
        }
      }
    });
  });

  observer.observe(inputElement.parentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['value']
  });

  if (previousStepsEl.dataset.update_reference_fields && previousStepsEl.dataset.update_reference_fields.length > 0) {
    attachUpdateReferenceDropdown(tagify, settings.whitelist);
  }

  return tagify
}

class EventDelegator {
  static setup() {
    // Handle input events for tagify inner inputs
    document.addEventListener('input', (event) => {
      if (event.target.classList.contains('tagify-inner-input')) {
        debouncedRefreshSize(event.target);
      }
    });

    document.addEventListener('keyup', (event) => {
      if (event.target.classList.contains('tagify-inner-input')) {
        debouncedRefreshSize(event.target);
      }
    });

    // Handle keydown events globally to prevent backspace from removing tags when input has content
    document.addEventListener('keydown', (event) => {
      if (event.target.classList.contains('tagify-inner-input')) {
        // If backspace is pressed and there's text in the input, prevent tag removal
        if (event.key === 'Backspace' && event.target.value.length > 0) {
          event.stopPropagation();
        }
      }
    });

    // Main click handler for schema dropdown
    document.addEventListener('click', (event) => {
      if (event.target.classList.contains('tagify-inner-input')) {
        event.stopPropagation();
        refreshSize(event.target);

        const tagElement = event.target.closest('.tagify__tag');
        if (tagElement) {
          const tagifyInstance = TagifyInstanceFinder.findForTag(tagElement);
          handleTagSchemaDropdown(tagElement, tagifyInstance);
        }
      } else {
        DropdownManager.closeAll();
      }
    });

    document.addEventListener('change', (event) => {
      if (event.target.classList.contains('tagify-inner-input')) {
        nestedTextInput(event.target);
      }
    });
  }
}

// Initialize event delegation when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  EventDelegator.setup();

  // Update field indicator components after Turbo updates
  const updateFieldIndicators = () => {
    // Wait for Turbo to finish updating the DOM
    setTimeout(() => {
      // Find all forms that have auto-submit controller or contain tagify fields
      const allForms = document.querySelectorAll('form');
      const forms = Array.from(allForms).filter(form => {
        return form.dataset.controller?.includes('auto-submit') ||
               form.querySelector('.tagify') !== null;
      });

      forms.forEach(form => {
        // Find all field indicator and reset section components in the form
        const indicators = form.querySelectorAll('[id^="field_changed_indicator_"], [id^="field_reset_section_"]');

        indicators.forEach(indicator => {
          const id = indicator.id;
          // Find the corresponding element in the new DOM
          const newElement = document.getElementById(id);
          if (newElement && newElement.innerHTML !== indicator.innerHTML) {
            // Update the content if it has changed
            indicator.innerHTML = newElement.innerHTML;
          }
        });
      });
    }, 50); // Small delay to ensure Turbo has finished updating
  };

  // Update field indicators after Turbo updates
  document.addEventListener('turbo:frame-load', updateFieldIndicators);
  document.addEventListener('turbo:submit-end', updateFieldIndicators);
  document.addEventListener('turbo:load', updateFieldIndicators);
});

function attachUpdateReferenceDropdown(tagify, originalWhitelist) {
  setTimeout(() => {
    if (tagify) {
      tagify.on('dropdown:show', () => {
        tagify.settings.whitelist = originalWhitelist.slice();
        const selectedValues = tagify.value.map(tag => tag.value);
        tagify.settings.whitelist = tagify.settings.whitelist.filter(item => !selectedValues.includes(item.value));
        tagify.dropdown.refilter();
      });
    }
  }, 0);
}

export function buildSettings(previous_steps, variable, update_reference_fields) {
  let whitelist = [];

  if (previous_steps.length > 0) {
    JSON.parse(previous_steps).forEach(step => whitelist.push(step));
  }

  if (update_reference_fields && update_reference_fields.length > 0) {
    JSON.parse(update_reference_fields).forEach(step => whitelist.push(step));
  }

  if (variable) {
    whitelist.push(JSON.parse(variable));
  }

  const transformUpdateReference = function(tag) {
    const whitelistedTag = whitelist.find(wt => wt.value === tag.value);

    if (whitelistedTag) {
      Object.assign(tag, whitelistedTag, { freeText: '' });
    } else {
      customTag(tag);
    }
  }

  const transformTag = function(tag) {
    const uuid = extractUuidAndFreetext(tag.value)[0];
    const freeText = extractUuidAndFreetext(tag.value)[1];
    const whitelistedTag = whitelist.find(wt => wt.value === uuid);

    if (whitelistedTag) {
      Object.assign(tag, whitelistedTag, { freeText: freeText });
    } else {
      customTag(tag);
    }
  }

  const settings = {
    whitelist: whitelist,
    mixTagsInterpolator: ["{{", "}}"],
    maxTags: 20,
    mode: 'mix',
    pattern: /\{\{\s*/, // '{{'
    tagTextProp: 'value',
    enforceWhitelist: false,
    skipInvalid: true,
    editTags: false,
    pasteAsTags: false,
    duplicates: true,
    backspace: true,
    transformTag: (update_reference_fields && update_reference_fields.length > 0)
      ? transformUpdateReference
      : transformTag,
    templates: {
      tag: tagTemplate,
      dropdownItem: suggestionItemTemplate
    },
    dropdown: buildDropDownOptions(),
  }

  return settings
}

export function buildInputValue(eventElement) {
  let inputValue = '';
  const inputElements = [].slice.call(eventElement.childNodes);

  inputElements.forEach(function(element) {
    if (element.hasOwnProperty('__tagifyTagData')) {
      if (element.__tagifyTagData.value === undefined) return;
      if (element.classList.contains('tagify__tag--hide')) return;

      const freeText = element.querySelector('.inner-input').value;
      inputValue += `{{ ${element.__tagifyTagData.value}${freeText} }}`
    } else if (element.textContent !== undefined) {
      inputValue += element.textContent;
    }
  });

  return clearValue(inputValue);
}

function extractUuidAndFreetext(rawStr) {
  const str = rawStr ? rawStr.trim() : "";
  const dotIndex = str.indexOf('.') === -1 ? str.length : str.indexOf('.');
  const spaceIndex = str.indexOf(' ') === -1 ? str.length : str.indexOf(' ');
  const pipeIndex = str.indexOf('|') === -1 ? str.length : str.indexOf('|');
  if (dotIndex === -1 && spaceIndex === -1 && pipeIndex === -1) {
    return [str, '']
  }
  const splitIndex = Math.min(dotIndex, spaceIndex, pipeIndex);
  const uuid = str.slice(0, splitIndex);
  const freeText = str.slice(splitIndex);
  return [uuid, freeText]
}

function clearValue(input) {
  return input.replace(/[\u200B-\u200D\uFEFF]/g, '').replace(/[\s\n\t]+$/g, '');
}

function customTag(tag) {
  const tagValue = tag.value ? tag.value : "";
  const splitText = tagValue.split('|').map(v => v.trim());
  const name = splitText.shift();
  splitText.unshift('')
  const freeText = splitText.join(' | ');
  Object.assign(tag, { freeText: freeText, name: name, value: name, avatar: '' });
}

function tagifyChange(event) {
  const tagifyInstance = event.detail.tagify;
  const originalInput = tagifyInstance.DOM.originalInput;

  const wrapper = originalInput.closest('.tagify-parent-wrapper');
  if (!wrapper) return;

  let targetElement = null;

  if (originalInput.dataset.targetName) {
    const targetName = originalInput.dataset.targetName;
    const allHiddenInputs = wrapper.querySelectorAll('input[type="hidden"]');

    for (const input of allHiddenInputs) {
      if (input.name === targetName && input.closest('.tagify-parent-wrapper') === wrapper) {
        targetElement = input;
        break;
      }
    }
  }

  if (!targetElement) return;

  const eventElement = tagifyInstance.DOM.input;
  targetElement.value = buildInputValue(eventElement);
  targetElement.dispatchEvent(new Event('input', { bubbles: true }));
}

function buildDropDownOptions() {
  return {
    position: 'all',
    highlightFirst: true,
    fuzzySearch: true,
    closeOnSelect: true,
    mapValueTo: 'value',
    maxItems: 99,
    enabled: 0,
    includeSelectedTags: true,
    searchKeys: ['value', 'name']
  }
}

export function calculateSize(element, value) {
  // For monospace fonts, use simple character-based calculation
  const charWidth = 13; // Approximate width of a monospace character in pixels
  const textWidth = (value || '').length * charWidth;

  // Add minimal padding
  const padding = 4;
  const minWidth = 20;
  const calculatedWidth = Math.max(textWidth + padding, minWidth);

  return calculatedWidth + 'px';
}

function tagTemplate(tagData) {
  return `
    <tag title="${tagData.name}"
      id="tag-${tagData.value}-${new Date().getTime()}"
      contenteditable='false'
      spellcheck='false'
      tabIndex="-1"
      class="tagify__tag ${tagData.class ? tagData.class : ""} justify-start"
      ${this.getAttributes(tagData)}>

      <div class="">
        <div class='mx-1'>
          ${tagData.avatar}
        </div>
        <div class="py-1"><span>${tagData.name}</span></div>
      </div>

      <div class="shrink${tagData.input_disabled ? ' hidden' : ''}">
        <div>
          <input
            class='inner-input tagify-inner-input box-border block min-w-8 font-mono'
            style="width: ${calculateSize(null, tagData.freeText)}"
            tabIndex='0'
            contenteditable='true'
            white-space='nowrap'
            value="${tagData.freeText.toHtmlEntities()}"
            data-hidden-input-name='${this.DOM.originalInput.dataset.targetName}'
            >
          </input>
        </div>
      </div>

      <x title='' class='tagify__tag__removeBtn grow-0' role='button' aria-label='remove tag' class="flex-none"></x>
    </tag>
    `
}

function nestedTextInput(element) {
  const wrapper = element.closest('.tagify-parent-wrapper');
  if (!wrapper) return;

  const tagsElement = wrapper.querySelector('tags');
  if (!tagsElement) return;

  const tagInput = tagsElement.querySelector('.tagify__input');
  if (!tagInput) return;

  const hiddenInputName = element.dataset.hiddenInputName;
  if (!hiddenInputName) return;

  // Find all hidden inputs in wrapper and match by name
  const allHiddenInputs = wrapper.querySelectorAll('input[type="hidden"]');
  let hiddenInput = null;

  for (const input of allHiddenInputs) {
    if (input.name === hiddenInputName && input.closest('.tagify-parent-wrapper') === wrapper) {
      hiddenInput = input;
      break;
    }
  }

  if (hiddenInput) {
    hiddenInput.value = buildInputValue(tagInput);
  }
}

// Debounce function to limit how often refreshSize is called
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

function getCharWidth() {
  let charWidth = 12; // Default fallback
  try {
    const testElement = document.createElement('span');
    testElement.classList.add('font-mono');
    testElement.style.visibility = 'hidden';
    testElement.style.position = 'absolute';
    testElement.style.whiteSpace = 'nowrap';
    testElement.textContent = 'M'.repeat(10);

    document.body.appendChild(testElement);
    const measuredWidth = testElement.offsetWidth;
    document.body.removeChild(testElement);

    charWidth = measuredWidth / 10;
  } catch (e) {
    // Fallback to default if measurement fails
    console.warn('Could not measure font, using default charWidth:', charWidth);
  }
  return charWidth;
}

function getTextWidth(value) {
  return value.length * getCharWidth();
}

function refreshSize(element) {
  if (!element) {
    return;
  }

  // Get the current value
  const value = element.value || '';

  // Calculate new width with minimal padding
  const padding = 20;
  const minWidth = 20;
  const newWidth = Math.max(getTextWidth(value) + padding, minWidth);

  // Get current width
  const currentWidth = parseInt(element.style.width) || 0;

  // Only update if there's a significant change
  if (Math.abs(currentWidth - newWidth) > 2) {
    element.style.width = newWidth + 'px';
  }
}

/**
 * Replace mix-mode content from a stored string (parse tags, unclosed-{{ handling).
 * @param {*} tagify — Tagify instance
 * @param {string} storedValue — serialized mix e.g. from hidden or paste merge
 * @param {{ syncHidden?: boolean }} [options] — if true, run tagifyChange so hidden input updates
 */
function reloadTagifyMixFromStoredString(tagify, storedValue, { syncHidden = false } = {}) {
  const prepared = prepareUnclosedMixTagOpeners(storedValue);
  tagify.loadOriginalValues(prepared.value);
  restoreUnclosedMixTagOpeners(tagify, prepared.token);

  queueMicrotask(() => {
    if (syncHidden) {
      tagifyChange({ detail: { tagify } });
    }
    const wrap = tagify.DOM.originalInput.closest('.tagify-parent-wrapper');
    wrap?.querySelectorAll('.tagify-inner-input').forEach((input) => {
      refreshSize(input);
    });
  });
}

/**
 * Re-render Tagify mix UI from each wrapper's hidden input (same as init).
 * Call after autosave so stored `{{ … }}` maps to tags without full page reload.
 * Note: may reset caret inside Tagify fields.
 *
 * @param {ParentNode} root — e.g. the step turbo-frame element
 */
export function resyncTagifyFieldsFromHiddenInRoot(root) {
  if (!root?.querySelectorAll) return;

  root.querySelectorAll('.tagify-parent-wrapper').forEach((wrapper) => {
    const tagifyInput = wrapper.querySelector('input.tagify');
    const tagify = tagifyInput?.__tagify;
    const targetName = tagifyInput?.dataset?.targetName;
    if (!tagify || !targetName) return;

    let hidden = null;
    for (const input of wrapper.querySelectorAll('input[type="hidden"]')) {
      if (
        input.name === targetName &&
        input.closest('.tagify-parent-wrapper') === wrapper
      ) {
        hidden = input;
        break;
      }
    }
    if (!hidden) return;

    reloadTagifyMixFromStoredString(tagify, hidden.value);
  });
}

// Debounced version of refreshSize
const debouncedRefreshSize = debounce(refreshSize, 50);

function suggestionItemTemplate(tagData) {
  return `
    <div ${this.getAttributes(tagData)}
      class='tagify__dropdown__item flex content-center items-center rounded-sm border ${tagData.class || ''}'
      tabindex="0"
      role="option">
      <div class='m-1 mr-2 flex-none'>
        ${tagData.avatar || ''}
      </div>
      <div class="flex-col flex-grow whitespace-normal">
        <strong>${tagData.name || ''}</strong>
        <span class="block">
          ${tagData.position ? tagData.position + ': ' : ''}${tagData.value}
        </span>
        ${tagData.description ? `<span class="block text-sm text-gray-500">${tagData.description}</span>` : ''}
      </div>
    </div>
  `
}

class SchemaDropdown {
  constructor(stepTag, tagify, tagElement = null) {
    this.stepTag = stepTag;
    this.tagify = tagify;
    this.tagElement = tagElement;
    this.dropdown = null;
    this.cleanupFunctions = [];
  }

  async show() {
    this.createDropdown();
    this.positionDropdown();
    this.setupEventListeners();
    DropdownManager.highlightTag(this.tagElement);

    try {
      const schema = await this.fetchSchema();
      const fieldOptions = generateFieldOptions(schema, this.stepTag.value);
      this.populateDropdown(fieldOptions, schema);
    } catch (error) {
      console.error('Error in SchemaDropdown.show():', error);
      this.cleanup();
    }
  }

  createDropdown() {
    this.dropdown = document.createElement('div');
    this.dropdown.className = 'schema-field-dropdown';
    this.dropdown.setAttribute('data-tag-element', this.tagElement);
    // Store reference to the tagify input element for click tracking
    if (this.tagify && this.tagify.DOM && this.tagify.DOM.originalInput) {
      this.dropdown.setAttribute('data-tagify-input', this.tagify.DOM.originalInput.dataset.targetName || '');
    }

    this.createHeader();
    this.createOptionsContainer();
    this.showLoadingState();
  }

  createHeader() {
    const header = document.createElement('div');
    header.className = 'schema-dropdown-header';
    header.innerHTML = `
      <div class="schema-dropdown-search-container">
        <div class="relative flex-1">
          <input type="text" class="schema-dropdown-search-input" />
          <svg class="schema-dropdown-search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
          </svg>
        </div>
        <button type="button" class="schema-dropdown-reload-btn" title="Reload schema">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
          </svg>
        </button>
      </div>
    `;
    this.dropdown.appendChild(header);

    // Add click handler for reload button
    const reloadButton = header.querySelector('button');
    reloadButton.addEventListener('click', () => {
      this.reloadSchema();
    });
  }

  createOptionsContainer() {
    const optionsContainer = document.createElement('div');
    optionsContainer.className = 'schema-dropdown-options-container';
    this.dropdown.appendChild(optionsContainer);
  }

  showLoadingState() {
    const optionsContainer = this.dropdown.querySelector('.schema-dropdown-options-container');
    const loadingElement = document.createElement('div');
    loadingElement.className = 'schema-dropdown-loading-state';
    loadingElement.innerHTML = `
      <div class="flex items-center justify-center">
        <svg class="schema-dropdown-loading-spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
    `;
    optionsContainer.appendChild(loadingElement);
  }

  positionDropdown() {
    const inputRect = this.tagify.DOM.input.getBoundingClientRect();

    this.dropdown.style.position = 'fixed';
    this.dropdown.style.left = `${inputRect.left - 10}px`;
    this.dropdown.style.width = `${inputRect.width + 20}px`;
    this.dropdown.style.top = `${inputRect.bottom + 5}px`;

    document.body.appendChild(this.dropdown);
  }

  async fetchSchema(forceReload = false) {
    const isVariable = this.stepTag.value === 'variables';

    let url;
    if (isVariable) {
      const workflowId = this.stepTag.workflow_id;
      url = `/workflows/${workflowId}/variables_schema`;
    } else {
      const stepId = this.stepTag.step_id;
      url = forceReload
        ? `/steps/${stepId}/output_schema?force_reload=true`
        : `/steps/${stepId}/output_schema`;
    }

    const response = await fetch(url);
    if (!response.ok) throw new Error('Failed to fetch schema');
    return await response.json();
  }

  async reloadSchema() {
    try {
      const optionsContainer = this.dropdown.querySelector('.schema-dropdown-options-container');
      if (optionsContainer) {
        optionsContainer.innerHTML = '';
      }
      this.showLoadingState();

      const schema = await this.fetchSchema(true);
      const fieldOptions = generateFieldOptions(schema, this.stepTag.value);
      this.populateDropdown(fieldOptions, schema);
    } catch (error) {
      const optionsContainer = this.dropdown.querySelector('.schema-dropdown-options-container');
      if (optionsContainer) {
        optionsContainer.innerHTML = '<div class="p-4 text-center text-error">Failed to reload schema</div>';
      }
    }
  }

  setupEventListeners() {
    this.setupScrollListener();
    this.setupClickOutsideListener();
    this.setupEscapeListener();
  }

  setupScrollListener() {
    const scrollHandler = (event) => {
      if (this.dropdown && this.dropdown.contains(event.target)) {
        return;
      }
      this.cleanup();
    };

    window.addEventListener('scroll', scrollHandler, true);
    document.addEventListener('scroll', scrollHandler, true);

    this.cleanupFunctions.push(() => {
      window.removeEventListener('scroll', scrollHandler, true);
      document.removeEventListener('scroll', scrollHandler, true);
    });
  }

  setupClickOutsideListener() {
    const clickOutsideHandler = (event) => {
      if (this.dropdown && !this.dropdown.contains(event.target) && !this.tagify.DOM.input.contains(event.target)) {
        this.cleanup();
      }
    };

    document.addEventListener('click', clickOutsideHandler);
    this.cleanupFunctions.push(() => {
      document.removeEventListener('click', clickOutsideHandler);
    });
  }

  setupEscapeListener() {
    const escapeHandler = (event) => {
      if (event.key === 'Escape') {
        this.cleanup();
      }
    };

    document.addEventListener('keydown', escapeHandler);
    this.cleanupFunctions.push(() => {
      document.removeEventListener('keydown', escapeHandler);
    });
  }

  populateDropdown(fieldOptions, schema) {
    this.removeLoadingState();

    const treeData = buildFieldTree(fieldOptions, this.stepTag.value, schema);
    const treeItems = this.createTreeItems(treeData);

    const optionsContainer = this.dropdown.querySelector('.schema-dropdown-options-container');
    optionsContainer.innerHTML = '';
    treeItems.forEach(item => optionsContainer.appendChild(item));

    this.setupSearchFunctionality();
    this.setupDropdownClickHandler();
    this.highlightCurrentPath();
  }

  createTreeItems(treeData, level = 0) {
    const items = [];

    treeData.forEach(node => {
      const item = this.createTreeItem(node, level);
      items.push(item);

      if (node.isExpandable) {
        const childrenContainer = this.createChildrenContainer(node, level + 1);
        items.push(childrenContainer);
      }
    });

    return items;
  }

  createTreeItem(node, level) {
    const item = document.createElement('div');
    item.className = 'schema-tree-item';

    // Set indentation
    const basePadding = 12;
    const chevronWidth = 12;
    const levelIndentation = level * 20;
    item.style.paddingLeft = `${basePadding + chevronWidth + levelIndentation}px`;

    // Store search data - separate path from name/key for smart matching
    const key = node.path ? node.path.split('.').pop() : '';
    const nameAndKey = [node.name, key, node.description].filter(Boolean).join(' ').toLowerCase();
    const fullPath = (node.path || '').toLowerCase();

    item.dataset.searchText = nameAndKey;  // For simple searches (no dots)
    item.dataset.searchPath = fullPath;     // For path searches (with dots)

    if (node.isExpandable) {
      this.setupExpandableItem(item, node);
    } else {
      this.setupLeafItem(item, node);
    }

    return item;
  }

  setupExpandableItem(item, node) {
    item.classList.add('expandable');

    const fieldType = getFieldTypeDisplay(node);
    const isSelectableObject = (node.type === 'object' || node.type === 'array') && node.isExpandable;

    item.innerHTML = `
      <div class="schema-tree-item-content">
        <span class="schema-tree-expand-icon">▶</span>
        <div class="schema-tree-item-main">
          <div class="schema-tree-item-name">${node.name}${fieldType ? ` <span class="schema-tree-item-type">(${fieldType})</span>` : ''}</div>
          ${node.description ? `<div class="schema-tree-item-description">${node.description}</div>` : ''}
        </div>
        ${isSelectableObject ? `<div class="schema-tree-select-icon" title="Select ${node.name}">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M9 11l3 3L22 4"></path>
            <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
          </svg>
        </div>` : ''}
      </div>
    `;

    // Setup expand/collapse handler
    item.addEventListener('click', (e) => {
      if (e.target.closest('.schema-tree-select-icon')) return;
      e.stopPropagation();
      this.toggleExpansion(item);
    });

    // Setup select icon handler
    const selectIcon = item.querySelector('.schema-tree-select-icon');
    if (selectIcon) {
      selectIcon.addEventListener('click', (e) => {
        e.stopPropagation();
        this.selectField(node.path);
      });
    }
  }

  setupLeafItem(item, node) {
    item.classList.add('field-item');

    const fieldType = getFieldTypeDisplay(node);

    item.innerHTML = `
      <div class="schema-tree-item-content">
        <span class="mr-2" style="width: 12px; flex-shrink: 0;"></span>
        <div class="schema-tree-item-main">
          <div class="schema-tree-item-name">${node.name}${fieldType ? ` <span class="schema-tree-item-type">(${fieldType})</span>` : ''}</div>
          ${node.description ? `<div class="schema-tree-item-description">${node.description}</div>` : ''}
        </div>
      </div>
    `;

    item.addEventListener('click', (e) => {
      e.stopPropagation();
      this.selectField(node.path);
    });
  }

  createChildrenContainer(node, level) {
    const childrenContainer = document.createElement('div');
    childrenContainer.className = 'schema-tree-children';

    const childItems = this.createTreeItems(node.children, level);
    childItems.forEach(child => childrenContainer.appendChild(child));

    return childrenContainer;
  }

  toggleExpansion(item) {
    const isExpanded = item.classList.contains('expanded');
    const expandIcon = item.querySelector('.schema-tree-expand-icon');
    const childrenContainer = item.nextElementSibling;

    if (isExpanded) {
      item.classList.remove('expanded');
      expandIcon.textContent = '▶';
      childrenContainer.style.display = 'none';
    } else {
      item.classList.add('expanded');
      expandIcon.textContent = '▼';
      childrenContainer.style.display = 'block';
      // Show direct children (they might be hidden from search)
      const directChildren = childrenContainer.querySelectorAll(':scope > .schema-tree-item');
      directChildren.forEach(child => {
        child.style.display = 'block';
      });
    }
  }

  selectField(path) {
    const dropdownTagElement = this.tagElement;
    if (!dropdownTagElement) return;

    // Format the display value
    let displayValue = path.replace(this.stepTag.value + '.', '');
    if (displayValue && !displayValue.startsWith('.')) {
      displayValue = '.' + displayValue;
    }

    // Update the tag's input
    const innerInput = dropdownTagElement.querySelector('.tagify-inner-input');
    innerInput.value = displayValue;
    innerInput.focus();
    refreshSize(innerInput);

    // Update tag data
    this.updateTagData(dropdownTagElement, displayValue);

    // Update hidden field
    this.updateHiddenField();

    // Close the dropdown after selection
    this.cleanup();
  }

  updateTagData(tagElement, displayValue) {
    // Update internal tag data
    if (tagElement.__tagifyTagData) {
      tagElement.__tagifyTagData.freeText = displayValue;
    }

    // Update tag in value array
    const tagIndex = this.findTagIndex(tagElement);
    if (tagIndex !== -1) {
      this.tagify.value[tagIndex].freeText = displayValue;
    }
  }

  findTagIndex(tagElement) {
    const tagElements = this.tagify.DOM.input.querySelectorAll('.tagify__tag');

    for (let i = 0; i < tagElements.length; i++) {
      if (tagElements[i] === tagElement) {
        return i;
      }
    }

    return -1;
  }

  updateHiddenField() {
    setTimeout(() => {
      const originalInput = this.tagify.DOM.originalInput;
      const wrapper = originalInput.closest('.tagify-parent-wrapper');
      if (!wrapper) return;

      let targetElement = null;

      if (originalInput.dataset.targetName) {
        const targetName = originalInput.dataset.targetName;
        const allHiddenInputs = wrapper.querySelectorAll('input[type="hidden"]');

        for (const input of allHiddenInputs) {
          if (input.name === targetName && input.closest('.tagify-parent-wrapper') === wrapper) {
            targetElement = input;
            break;
          }
        }
      }

      if (targetElement) {
        const newValue = buildInputValue(this.tagify.DOM.input);
        targetElement.value = newValue;
        targetElement.dispatchEvent(new Event('change', { bubbles: true }));
        targetElement.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }, 0);
  }

  removeLoadingState() {
    const loadingElement = this.dropdown.querySelector('.schema-dropdown-loading-state');
    if (loadingElement) {
      loadingElement.remove();
    }
  }

  setupSearchFunctionality() {
    const searchInput = this.dropdown.querySelector('input[type="text"]');
    if (!searchInput) return;

    searchInput.addEventListener('input', (e) => {
      this.handleSearch(e.target.value.toLowerCase());
    });

    searchInput.addEventListener('click', (e) => {
      e.stopPropagation();
    });

    searchInput.addEventListener('focus', (e) => {
      e.stopPropagation();
    });
  }

  handleSearch(searchTerm) {
    const optionsContainer = this.dropdown.querySelector('.schema-dropdown-options-container');
    const allTreeItems = optionsContainer.querySelectorAll('.schema-tree-item');

    if (searchTerm === '') {
      this.resetSearchResults(allTreeItems);
      return;
    }

    this.performSearch(allTreeItems, searchTerm);
  }

  resetSearchResults(allTreeItems) {
    allTreeItems.forEach(item => {
      item.style.display = 'block';
      item.classList.remove('expanded', 'bg-primary/20');
      const expandIcon = item.querySelector('.schema-tree-expand-icon');
      if (expandIcon) expandIcon.textContent = '▶';
      const childrenContainer = item.nextElementSibling;
      if (childrenContainer && childrenContainer.classList.contains('schema-tree-children')) {
        childrenContainer.style.display = 'none';
      }
    });
  }

  performSearch(allTreeItems, searchTerm) {
    // Reset all items: hide and collapse
    allTreeItems.forEach(item => {
      item.style.display = 'none';
      item.classList.remove('bg-primary/20', 'expanded');
      const expandIcon = item.querySelector('.schema-tree-expand-icon');
      if (expandIcon) expandIcon.textContent = '▶';
      const childrenContainer = item.nextElementSibling;
      if (childrenContainer && childrenContainer.classList.contains('schema-tree-children')) {
        childrenContainer.style.display = 'none';
      }
    });

    // Determine search mode: path search (contains dots) or simple search
    const isPathSearch = searchTerm.includes('.');

    // Show matching items and expand their ancestors
    allTreeItems.forEach(item => {
      const isMatch = this.itemMatches(item, searchTerm, isPathSearch);

      if (isMatch) {
        item.style.display = 'block';
        // Expand all ancestors so this item is visible in the tree
        this.expandAncestors(item);

        // Highlight: for path search only exact match, for simple search all matches
        const isExactMatch = isPathSearch
          ? (item.dataset.searchPath || '') === searchTerm
          : true;
        if (isExactMatch) {
          item.classList.add('bg-primary/20');
        }
      }
    });
  }

  itemMatches(item, searchTerm, isPathSearch) {
    if (isPathSearch) {
      // For path searches, partial match to show in results
      const searchPath = item.dataset.searchPath || '';
      return searchPath.includes(searchTerm);
    } else {
      // For simple searches, match against name/key/description
      const searchText = item.dataset.searchText || '';
      return searchText.includes(searchTerm);
    }
  }

  expandAncestors(item) {
    const optionsContainer = this.dropdown.querySelector('.schema-dropdown-options-container');
    let parent = item.parentElement;

    while (parent && parent !== optionsContainer) {
      if (parent.classList.contains('schema-tree-children')) {
        parent.style.display = 'block';
        const parentItem = parent.previousElementSibling;
        if (parentItem && parentItem.classList.contains('schema-tree-item')) {
          parentItem.style.display = 'block';
          parentItem.classList.add('expanded');
          const expandIcon = parentItem.querySelector('.schema-tree-expand-icon');
          if (expandIcon) expandIcon.textContent = '▼';
        }
      }
      parent = parent.parentElement;
    }
  }

  highlightCurrentPath() {
    if (!this.tagElement) return;

    const innerInput = this.tagElement.querySelector('.tagify-inner-input');
    if (!innerInput || !innerInput.value) return;

    let inputValue = innerInput.value.trim();
    if (!inputValue) return;

    inputValue = inputValue.replace(/^\./, '');

    const searchTerm = inputValue.split(' ')[0];

    if (!searchTerm) return;

    const searchInput = this.dropdown.querySelector('.schema-dropdown-search-input');
    if (searchInput) {
      searchInput.value = searchTerm;
      this.handleSearch(searchTerm);
    }
  }

  setupDropdownClickHandler() {
    this.dropdown.addEventListener('click', (e) => {
      e.stopPropagation();
    });
  }

  cleanup() {
    DropdownManager.highlightTag(null); // Remove highlighting
    if (this.dropdown && this.dropdown.parentElement) {
      if (document.body.contains(this.dropdown)) {
        document.body.removeChild(this.dropdown);
      }
    }
    this.cleanupFunctions.forEach(cleanup => cleanup());
    this.cleanupFunctions = [];
  }
}

// Schema field selection functionality
async function showSchemaFields(stepTag, tagify, tagElement = null) {
  // Close any existing dropdowns for different tags before opening a new one
  const existingDropdowns = document.querySelectorAll('.schema-field-dropdown');

  existingDropdowns.forEach(dropdown => {
    const dropdownTagElement = dropdown.getAttribute('data-tag-element');
    if (dropdownTagElement !== tagElement && document.body.contains(dropdown)) {
      try {
        document.body.removeChild(dropdown);
      } catch (error) {
        console.warn('Could not remove dropdown:', error);
      }
    }
  });

  // Create and show the dropdown using the new SchemaDropdown class
  const schemaDropdown = new SchemaDropdown(stepTag, tagify, tagElement);
  await schemaDropdown.show();
}

function determineTypeFromAnyOfOneOf(property) {
  if (!property) return null;

  const anyOfArray = property.anyOf || property.oneOf;
  if (!Array.isArray(anyOfArray) || anyOfArray.length === 0) {
    return null;
  }

  // Check what types are in the anyOf/oneOf array
  const types = [];

  anyOfArray.forEach(subSchema => {
    if (subSchema && typeof subSchema === 'object') {
      if (subSchema.const !== undefined) {
        // For const values, infer type from the const value
        const constType = typeof subSchema.const;
        if (constType === 'string' || constType === 'number' || constType === 'boolean') {
          if (!types.includes(constType)) {
            types.push(constType);
          }
        }
      } else if (subSchema.type) {
        if (!types.includes(subSchema.type)) {
          types.push(subSchema.type);
        }
      }
    }
  });

  // Return the types joined with " | " for better readability
  if (types.length === 0) {
    return null;
  } else if (types.length === 1) {
    return types[0];
  } else {
    // Multiple types - join them with " | "
    return types.join(' | ');
  }
}

function generateConstValuesDescription(property) {
  if (!property) return '';

  const anyOfArray = property.anyOf || property.oneOf;
  if (!Array.isArray(anyOfArray) || anyOfArray.length === 0) return '';

  // Extract only const values (not objects or null)
  const constValues = [];
  anyOfArray.forEach(subSchema => {
    if (subSchema && typeof subSchema === 'object' && subSchema.const !== undefined) {
      constValues.push({
        value: subSchema.const,
        title: subSchema.title || String(subSchema.const).charAt(0).toUpperCase() + String(subSchema.const).slice(1)
      });
    }
  });

  if (constValues.length === 0) return '';

  const allOptions = constValues.map(option => {
    return `• ${option.value}${option.title !== String(option.value) ? ` (${option.title})` : ''}`;
  });

  const possibleValuesText = globalThis.I18n.t('schema.possible_values');

  if (allOptions.length <= 5) {
    return `${possibleValuesText}<br><br>${allOptions.join('<br>')}`;
  } else {
    const displayedOptions = allOptions.slice(0, 5);
    const remainingCount = allOptions.length - 5;
    const andMoreText = globalThis.I18n.t('schema.and_more_options', { count: remainingCount });
    return `${possibleValuesText}<br><br>${displayedOptions.join('<br>')}<br><br>${andMoreText}`;
  }
}

function generateEnumDescription(items) {
  if (!items.anyOf && !items.oneOf) return '';

  // In JSON Schema, anyOf/oneOf are always arrays
  const enumOptions = items.anyOf || items.oneOf;
  if (!Array.isArray(enumOptions) || enumOptions.length === 0) return '';

  const allOptions = enumOptions.map(option => {
    const title = option.title || option.const;
    const value = option.const;
    return `• ${value} (${title})`;
  });

  const possibleValuesText = globalThis.I18n.t('schema.possible_values');

  if (allOptions.length <= 5) {
    return `${possibleValuesText}<br><br>${allOptions.join('<br>')}`;
  } else {
    const displayedOptions = allOptions.slice(0, 5);
    const remainingCount = allOptions.length - 5;
    const andMoreText = globalThis.I18n.t('schema.and_more_options', { count: remainingCount });
    return `${possibleValuesText}<br><br>${displayedOptions.join('<br>')}<br><br>${andMoreText}`;
  }
}

function addArrayItemOptions(options, firstItemName, lastItemName, stepKey, currentPath, type, isSimpleEnumArray, enumDescription = '') {
  // First item
  options.push({
    name: firstItemName,
    value: `${stepKey}.${currentPath}.first`,
    description: '',
    type: type,
    path: `${currentPath}.first`,
    stepKey: stepKey,
    isArrayItem: true,
    isSimpleEnumArray: isSimpleEnumArray,
    enumDescription: enumDescription
  });

  // Last item
  options.push({
    name: lastItemName,
    value: `${stepKey}.${currentPath}.last`,
    description: '',
    type: type,
    path: `${currentPath}.last`,
    stepKey: stepKey,
    isArrayItem: true,
    isSimpleEnumArray: isSimpleEnumArray,
    enumDescription: enumDescription
  });
}

function generateFieldOptions(schema, stepKey, path = '') {
  const options = [];

  if (schema.type === 'object' && schema.properties) {
    Object.entries(schema.properties).forEach(([key, prop]) => {
      const currentPath = path ? `${path}.${key}` : key;

      // Add selectable fields
      // Skip creating wrapper field if prop has anyOf/oneOf with an object type,
      // since the object is expanded into its own branch instead. A union of
      // primitives keeps the wrapper and shows every type it can hold.
      const anyOfArray = prop.anyOf || prop.oneOf;
      const hasObjectInAnyOf = Array.isArray(anyOfArray) && anyOfArray.some(subSchema =>
        subSchema && subSchema.const === undefined &&
        subSchema.type === 'object' && subSchema.properties
      );

      if (isSelectableField(prop) && !hasObjectInAnyOf) {
        // Determine type - if prop has oneOf/anyOf, determine type from that
        let fieldType = prop.type;
        if (!fieldType && (prop.anyOf || prop.oneOf)) {
          fieldType = determineTypeFromAnyOfOneOf(prop);
        }

        // Add description with possible const values if oneOf/anyOf contains only const values
        let description = prop.description || '';
        const constValuesDescription = generateConstValuesDescription(prop);
        if (constValuesDescription) {
          if (description) {
            description = `${description}<br><br>${constValuesDescription}`;
          } else {
            description = constValuesDescription;
          }
        }

        options.push({
          name: prop.title || key,
          value: `${stepKey}.${currentPath}`,
          description: description,
          type: fieldType,
          path: currentPath,
          stepKey: stepKey
        });
      }

      // Handle anyOf/oneOf with object schemas and primitive types (not just const values)
      // In JSON Schema, anyOf/oneOf are always arrays
      if (Array.isArray(anyOfArray) && anyOfArray.length > 0) {
        anyOfArray.forEach((subSchema) => {
          // Skip const values (already handled above)
          if (subSchema && subSchema.const === undefined) {
            if (subSchema.type === 'object' && subSchema.properties) {
              // Recursively process object schemas in anyOf/oneOf
              options.push(...generateFieldOptions(subSchema, stepKey, currentPath));
            } else if (hasObjectInAnyOf && subSchema.type && subSchema.type !== 'array') {
              options.push({
                name: subSchema.title || prop.title || key,
                value: `${stepKey}.${currentPath}`,
                description: subSchema.description || prop.description || '',
                type: subSchema.type,
                path: currentPath,
                stepKey: stepKey
              });
            }
          }
        });
      }

      // Recursively add nested fields
      if (prop.type === 'object' && prop.properties && Object.keys(prop.properties).length > 0) {
        options.push(...generateFieldOptions(prop, stepKey, currentPath));
      } else if (prop.type === 'array' && prop.items) {
        // Check if array items are simple enum values (not objects with properties)
        const isSimpleEnum = prop.items.anyOf || prop.items.oneOf || prop.items.enum;

        const firstItemName = window.I18n.t('schema.first');
        const lastItemName = window.I18n.t('schema.last');

        if (isSimpleEnum) {
          // For simple enum arrays, create first/last as selectable fields
          const enumDescription = generateEnumDescription(prop.items);

          addArrayItemOptions(options, firstItemName, lastItemName, stepKey, currentPath, prop.items.type || 'string', true, enumDescription);
        } else {
          // For object arrays, create first/last as objects and recursively process
          addArrayItemOptions(options, firstItemName, lastItemName, stepKey, currentPath, prop.items.type, false);

          options.push(...generateFieldOptions(prop.items, stepKey, `${currentPath}.first`));
          options.push(...generateFieldOptions(prop.items, stepKey, `${currentPath}.last`));
        }
      }
    });
  }

  return options;
}

function isSelectableField(prop) {
  // Empty object
  if (prop.type === 'object' && prop.properties && Object.keys(prop.properties).length === 0) {
    return true;
  }

  // Non-object or object without properties (but not arrays)
  if ((prop.type !== 'object' || !prop.properties) && prop.type !== 'array') {
    return true;
  }

  return false;
}

function buildFieldTree(fieldOptions, stepKey, schema) {
  const tree = [];

  fieldOptions.forEach(option => {
    addToTree(tree, option, stepKey, schema);
  });

  return tree;
}

function unionObjectVariant(schema) {
  const variants = schema?.anyOf || schema?.oneOf;
  if (!Array.isArray(variants)) return schema;

  return variants.find(variant =>
    variant && variant.const === undefined && variant.type === 'object' && variant.properties
  ) || schema;
}

function schemaAtPath(schema, path) {
  if (!schema || !path) return null;

  const pathParts = path.split('.');
  let currentSchema = schema;

  for (const part of pathParts) {
    currentSchema = unionObjectVariant(currentSchema);

    if (part === 'first' || part === 'last') {
      if (currentSchema && currentSchema.type === 'array' && currentSchema.items) {
        currentSchema = currentSchema.items;
      } else {
        return null;
      }
    } else if (currentSchema.properties && currentSchema.properties[part]) {
      currentSchema = currentSchema.properties[part];
    } else {
      return null;
    }
  }

  return currentSchema;
}

function getPropertyFromPath(schema, path, property) {
  const currentSchema = schemaAtPath(schema, path);
  if (!currentSchema) return '';

  // An expandable node represents the object variant of a union, so prefer its
  // metadata over the wrapper's.
  return unionObjectVariant(currentSchema)[property] || currentSchema[property] || '';
}

function addToTree(tree, option, stepKey, schema) {
  const pathParts = parsePath(option.path);
  let currentLevel = tree;
  let currentPath = '';

  for (let i = 0; i < pathParts.length; i++) {
    const part = pathParts[i];
    const isLastPart = i === pathParts.length - 1;

    currentPath = currentPath ? `${currentPath}${part.startsWith('[') ? part : '.' + part}` : part;

    if (part === 'first' || part === 'last') {
      const displayName = part === 'first' ? window.I18n.t('schema.first') : window.I18n.t('schema.last');

      const isSimpleEnumArray = option.isSimpleEnumArray;

      if (isSimpleEnumArray) {
        findOrCreateNode(currentLevel, displayName, 'field', currentPath, option.value, option.description, false, false, option.type);
      } else {
        const itemNode = findOrCreateNode(currentLevel, displayName, 'object', currentPath, stepKey, '', true, true);
        currentLevel = itemNode.children;
      }
    } else {
      if (isLastPart) {
        // Final field
        if (option.isArrayItem) {
          if (option.isSimpleEnumArray) {
            findOrCreateNode(currentLevel, option.name, 'field', option.path, option.value, option.description, false, false, option.type);
          } else {
            findOrCreateNode(currentLevel, option.name, 'object', option.path, option.value, option.description, true, true);
          }
        } else {
          findOrCreateNode(currentLevel, option.name, 'field', option.path, option.value, option.description, false, false, option.type);
        }
      } else {
        // Intermediate object/array
        const nextPart = pathParts[i + 1];
        const shouldBeArray = nextPart && nextPart.includes('[') && nextPart.includes(']');
        const nodeType = shouldBeArray ? 'array' : 'object';
        const originalDescription = getPropertyFromPath(schema, currentPath, 'description');
        const title = getPropertyFromPath(schema, currentPath, 'title');

        // Check if this is an array with enum items and add enum description
        let description = originalDescription;
        const currentSchema = schemaAtPath(schema, currentPath);

        if (currentSchema && currentSchema.type === 'array' && currentSchema.items && (currentSchema.items.anyOf || currentSchema.items.oneOf)) {
          const enumDescription = generateEnumDescription(currentSchema.items);

          if (originalDescription && enumDescription) {
            description = `${originalDescription}<br><br>${enumDescription}`;
          } else if (enumDescription) {
            description = enumDescription;
          }
        }
        const displayName = title || part;
        const node = findOrCreateNode(currentLevel, displayName, nodeType, currentPath, '', description, true, false);
        currentLevel = node.children;
      }
    }
  }
}

function findOrCreateNode(currentLevel, name, type, path, value, description, isExpandable, isSelectable, fieldType = null) {
  let node = currentLevel.find(n => n.name === name && n.type === type);

  if (!node) {
    node = {
      name,
      type,
      path,
      value: value || '',
      description,
      children: [],
      isExpandable,
      isSelectable,
      fieldType
    };
    currentLevel.push(node);
  }

  return node;
}

function parsePath(path) {
  const pathParts = [];
  let currentPart = '';
  let bracketDepth = 0;

  for (let i = 0; i < path.length; i++) {
    const char = path[i];
    if (char === '[') {
      bracketDepth++;
      if (bracketDepth === 1 && currentPart) {
        pathParts.push(currentPart);
        currentPart = '';
      }
      currentPart += char;
    } else if (char === ']') {
      bracketDepth--;
      currentPart += char;
      if (bracketDepth === 0) {
        pathParts.push(currentPart);
        currentPart = '';
      }
    } else if (char === '.' && bracketDepth === 0) {
      if (currentPart) {
        pathParts.push(currentPart);
        currentPart = '';
      }
    } else {
      currentPart += char;
    }
  }
  if (currentPart) {
    pathParts.push(currentPart);
  }

  return pathParts;
}

function getFieldTypeDisplay(node) {
  if (node.type === 'field') {
    if (!node.fieldType) return node.fieldType;

    // A union field carries every type it can hold, e.g. "string | null".
    return node.fieldType
      .split(' | ')
      .map(type => (type === 'number' ? 'float' : type))
      .join(' | ');
  } else if (node.type === 'object') {
    return 'object';
  } else if (node.type === 'array') {
    return 'array';
  }
  return null;
}
